import {
  useEffect, useMemo,
} from 'react';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import { useRequiresCleanMap } from '~/_shared/utils/hooks/useRequiresCleanMap';
import { areBoundaryLabelsVisible } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryTerritoryGroup.helpers';
import { type BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { useBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import {
  hasBoundaryItemChanged, hasZoomLevelDetailChanged,
} from '~/store/boundaryItems/boundaryItems.helpers';
import { useMapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import {
  useActiveBoundaryTerritoryGroupsSelector, usePrimaryBoundaryGroupIdSelector,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { useMapComponentZoomSelector } from '~/store/frontendState/mapComponent/mapComponent.selectors';
import { useBoundarySelectModalOpenSelector } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.selectors';
import { isBoundarySelectUsedForEditing } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import { type MapBoundaryManager } from '../../mapBoundary.manager';
import { useMapBoundaryDistanceToPixels } from './useMapBoundaryDistanceToPixels';
import { useMapBoundaryLabelContent } from './useMapBoundaryLabelContent';
import { useMapBoundaryLabelFilter } from './useMapBoundaryLabelFilter';
import { useMapBoundaryPolygonFilter } from './useMapBoundaryPolygonFilter';

export const useMapBoundaryPolygons = (manager?: MapBoundaryManager, map?: google.maps.Map) => {
  const activeBoundaryTerritoryGroups = useActiveBoundaryTerritoryGroupsSelector();
  const boundaryItems = useMapBoundariesSelector();
  const boundaryGroups = useBoundaryGroupsSelector();
  const mapZoom = useMapComponentZoomSelector();
  const boundarySelect = useBoundarySelectModalOpenSelector();
  const { createLabelContentResolver } = useMapBoundaryLabelContent();

  const cleanMapRequired = useRequiresCleanMap();
  const previousCleanMapRequired = usePrevious(cleanMapRequired);

  const btgIdUsedUsedInEditing = (boundarySelect && isBoundarySelectUsedForEditing(boundarySelect))
    ? boundarySelect.selectionModeData.boundaryTerritoryGroupId
    : null;

  const previousManager = usePrevious(manager);
  const forceUpdate = (!previousManager && previousManager !== manager) || (previousCleanMapRequired !== cleanMapRequired);

  const previousMapZoom = usePrevious(mapZoom);
  const previousBoundaryItems = usePrevious(boundaryItems);

  const boundaryPolygonFilters = useMapBoundaryPolygonFilter();
  const previousBoundaryPolygonFilters = usePrevious(boundaryPolygonFilters);

  const boundaryGroupsWithLabels = useMemo(() =>
    activeBoundaryTerritoryGroups
      .filter(areBoundaryLabelsVisible)
      .map(btg => btg.boundaryGroupId)
      .filter(id => id !== boundarySelect?.selectBoundaryGroupId)
  , [boundarySelect?.selectBoundaryGroupId, activeBoundaryTerritoryGroups]);

  const distanceToPixelsScale = useMapBoundaryDistanceToPixels(map);
  const boundaryLabelFilters = useMapBoundaryLabelFilter({
    boundaryGroupIds: boundaryGroupsWithLabels,
    distanceToPixelsScale,
    textDimensionsCalculator: manager?.textDimensionsCalculator,
    createLabelContentResolver,
  });
  const previousBoundaryLabelFilters = usePrevious(boundaryLabelFilters);

  const primaryBoundaryGroupId = usePrimaryBoundaryGroupIdSelector();
  const primaryBoundaryGroup = activeBoundaryTerritoryGroups.find(g => g.boundaryGroupId === primaryBoundaryGroupId);

  // Remove polygons when boundaries are removed from the map
  useEffect(() => {
    if (!manager) {
      return;
    }

    const usedBoundaries = new Set(activeBoundaryTerritoryGroups.map(g => g.boundaryGroupId));
    manager.removeUnusedBoundaryGroups(usedBoundaries);
  }, [activeBoundaryTerritoryGroups, manager]);

  useEffect(() => {
    if (!manager || !mapZoom || !primaryBoundaryGroup) {
      return;
    }

    for (const boundaryTerritoryGroup of activeBoundaryTerritoryGroups) {
      const boundaryGroup = boundaryGroups.find(g => g.id === boundaryTerritoryGroup.boundaryGroupId);
      if (!boundaryGroup) {
        continue;
      }

      const shouldUpdate = forceUpdate
        || hasBoundaryItemChanged(boundaryGroup.id, boundaryItems, previousBoundaryItems)
        || hasZoomLevelDetailChanged(boundaryGroup, mapZoom, previousMapZoom)
        || hasBoundaryFilterChanged(boundaryGroup.id, boundaryPolygonFilters, previousBoundaryPolygonFilters)
        || hasBoundaryFilterChanged(boundaryGroup.id, boundaryLabelFilters, previousBoundaryLabelFilters);

      if (!shouldUpdate) {
        continue;
      }

      const boundariesData = boundaryItems.get(boundaryTerritoryGroup.boundaryGroupId);
      if (!boundariesData) {
        continue;
      }

      const emptyFilter = () => false;
      const emptyLabelFilter = () => null;

      const hideAll = cleanMapRequired
        && boundaryTerritoryGroup.boundaryTerritoryGroupId !== btgIdUsedUsedInEditing;

      const { filter, labelFilter } = hideAll ? {
        filter: emptyFilter,
        labelFilter: emptyLabelFilter,
      } : {
        filter: boundaryPolygonFilters.get(boundaryGroup.id) ?? emptyFilter,
        labelFilter: boundaryLabelFilters.get(boundaryGroup.id) ?? emptyLabelFilter,
      };

      manager.updateBoundaryGroupPolygons({ boundaryGroup, boundariesData, mapZoomLevel: mapZoom, filter, labelFilter });
    }

  },
  [boundaryGroups, boundaryItems, boundaryLabelFilters, boundaryPolygonFilters, activeBoundaryTerritoryGroups, forceUpdate,
    manager, mapZoom, previousBoundaryItems, previousBoundaryLabelFilters, previousBoundaryPolygonFilters, previousMapZoom,
    primaryBoundaryGroup, cleanMapRequired, btgIdUsedUsedInEditing]);
};

const hasBoundaryFilterChanged = (boundaryGroupId: number, currentFilters: ReadonlyMap<BoundaryGroupId, any>, previousFilters?: ReadonlyMap<BoundaryGroupId, any>) => {
  const current = currentFilters.get(boundaryGroupId);
  const previous = previousFilters?.get(boundaryGroupId);

  return current !== previous;
};
